/*
 * Convert to Infographic — region merge (parity port of the DecKorate POC's
 * InfographicBuilder.buildInfographicReplacingShapes).
 *
 * When the source was a selected table/text shape (not the whole slide), the
 * infographic shouldn't just get appended as a bare new slide — it should
 * take over the space the selection occupied. This reads the LIVE presentation
 * bytes, clones the active slide's XML, removes the shapes the selection
 * named (or, failing a name match, any shape overlapping the target region by
 * more than 50%), wraps the composer's shape XML in a <p:grpSp> positioned at
 * that region, and re-zips the whole deck. The result is inserted as a NEW
 * slide right after the original (via `sourceSlideIds` in
 * insertSlidesFromBase64) — the original slide is never touched, so a failed
 * or unwanted result costs nothing.
 */

import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import { NS_P } from "../../../const";
import {
  getActivePresentationBytes,
  getActiveSlideIndex,
  parseSlideFromZip,
} from "../../FormatTools/services/slideOoxml";
import { sanitizeComposerXml, u8ToBase64 } from "./infographicInsert";

export interface RegionGeometryEmu {
  readonly x: number;
  readonly y: number;
  readonly cx: number;
  readonly cy: number;
}

export interface InfographicMergeResult {
  readonly base64: string;
  /** Always resolved — mergeInfographicIntoRegion throws otherwise. */
  readonly sourceSlideId: string;
}

const SP_PATTERN = /<p:sp\b[\s\S]*?<\/p:sp>/g;
const CXN_PATTERN = /<p:cxnSp\b[\s\S]*?<\/p:cxnSp>/g;
const GRP_PATTERN = /<p:grpSp\b[\s\S]*?<\/p:grpSp>/g;
const GRAPHIC_FRAME_PATTERN = /<p:graphicFrame\b[\s\S]*?<\/p:graphicFrame>/g;
const OFF_PATTERN = /<a:off\s+x="(-?\d+)"\s+y="(-?\d+)"/;
const EXT_PATTERN = /<a:ext\s+cx="(\d+)"\s+cy="(\d+)"/;
const CNVPR_NAME_PATTERN = /cNvPr[^>]*\bname="([^"]*)"/;

/** Overlap threshold used to remove an unnamed shape sitting under the target region. */
const OVERLAP_REMOVE_THRESHOLD = 0.5;

/** Id the injected group's shapes are renumbered from — clear of the
 *  "Infographic Group" wrapper's own id (9500, below) and any realistic id
 *  already used by shapes left on the slide, so the merged slide can never
 *  end up with two shapes sharing an id (invalid OOXML PowerPoint rejects). */
const INJECTED_SHAPE_START_ID = 9501;

interface BoundingBox {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

function extractShapeElements(xml: string): string[] {
  const sp = [...xml.matchAll(SP_PATTERN)].map((m) => m[0]);
  const cxn = [...xml.matchAll(CXN_PATTERN)].map((m) => m[0]);
  const grp = [...xml.matchAll(GRP_PATTERN)].map((m) => m[0]);
  return [...sp, ...cxn, ...grp];
}

function computeChildBoundingBox(elements: readonly string[]): BoundingBox | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const el of elements) {
    const off = OFF_PATTERN.exec(el);
    const ext = EXT_PATTERN.exec(el);
    if (!off || !ext) continue;
    const x = Number(off[1]);
    const y = Number(off[2]);
    const cx = Number(ext[1]);
    const cy = Number(ext[2]);
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + cx);
    maxY = Math.max(maxY, y + cy);
  }
  return Number.isFinite(minX) ? { minX, minY, maxX, maxY } : null;
}

function buildGroupXml(elements: readonly string[], region: RegionGeometryEmu, bbox: BoundingBox): string {
  const childW = bbox.maxX - bbox.minX || 1;
  const childH = bbox.maxY - bbox.minY || 1;
  return (
    `<p:grpSp>` +
    `<p:nvGrpSpPr><p:cNvPr id="9500" name="Infographic Group"/><p:cNvGrpSpPr/><p:nvPr/></p:nvGrpSpPr>` +
    `<p:grpSpPr><a:xfrm>` +
    `<a:off x="${region.x}" y="${region.y}"/><a:ext cx="${region.cx}" cy="${region.cy}"/>` +
    `<a:chOff x="${bbox.minX}" y="${bbox.minY}"/><a:chExt cx="${childW}" cy="${childH}"/>` +
    `</a:xfrm></p:grpSpPr>` +
    elements.join("") +
    `</p:grpSp>`
  );
}

/** True when an unnamed shape overlaps the target region by more than 50% of its own area. */
function shapeOverlapsRegion(el: string, region: RegionGeometryEmu): boolean {
  const off = OFF_PATTERN.exec(el);
  const ext = EXT_PATTERN.exec(el);
  if (!off || !ext) return false;
  const sx = Number(off[1]);
  const sy = Number(off[2]);
  const scx = Number(ext[1]);
  const scy = Number(ext[2]);
  const overlapX = Math.max(0, Math.min(sx + scx, region.x + region.cx) - Math.max(sx, region.x));
  const overlapY = Math.max(0, Math.min(sy + scy, region.y + region.cy) - Math.max(sy, region.y));
  const shapeArea = scx * scy;
  return shapeArea > 0 && (overlapX * overlapY) / shapeArea > OVERLAP_REMOVE_THRESHOLD;
}

/** Match by cNvPr name first; fall back to geometry overlap when no name matches. */
function shouldRemoveShape(el: string, nameSet: ReadonlySet<string>, region: RegionGeometryEmu): boolean {
  const nameMatch = CNVPR_NAME_PATTERN.exec(el);
  if (nameMatch && nameSet.size > 0 && nameSet.has(nameMatch[1]!.toLowerCase())) return true;
  if (nameSet.size === 0 || !nameMatch) return shapeOverlapsRegion(el, region);
  return false;
}

function removeMatchingShapes(
  slideXml: string,
  pattern: RegExp,
  nameSet: ReadonlySet<string>,
  region: RegionGeometryEmu,
): string {
  // matchAll(), not exec() — exec() on a fresh copy of the `g`-flagged pattern
  // only returns the first match, silently leaving later matching shapes behind.
  const matches = [...slideXml.matchAll(pattern)].map((m) => m[0]);
  let xml = slideXml;
  for (const el of matches) {
    if (shouldRemoveShape(el, nameSet, region)) xml = xml.replace(el, "");
  }
  return xml;
}

/** The numeric <p:sldId id="..."> for the slide at `slideIndex` (1-based). */
function getSldIdAt(presentationXml: string, slideIndex: number): string | null {
  const doc = new DOMParser().parseFromString(presentationXml, "text/xml");
  const sldIds = doc.getElementsByTagNameNS(NS_P, "sldId");
  if (slideIndex < 1 || slideIndex > sldIds.length) return null;
  return sldIds[slideIndex - 1]?.getAttribute("id") ?? null;
}

export const NO_MERGEABLE_SHAPES_MESSAGE =
  "The infographic service didn't return any shapes to place. Nothing was changed — try again.";

/**
 * Wraps the backend's shape XML into a <p:grpSp> at `region`, removes the
 * shapes named in `shapeNames` (or, failing that, anything overlapping the
 * region) from a CLONE of the active slide, and returns the whole deck
 * (base64) plus the original slide's numeric id — the base64 is meant to be
 * inserted with `sourceSlideIds: [sourceSlideId]` so only that one modified
 * slide is pulled in, right after the untouched original.
 */
export async function mergeInfographicIntoRegion(
  spTreeXml: string,
  region: RegionGeometryEmu,
  shapeNames: readonly string[],
): Promise<InfographicMergeResult> {
  const sanitizedXml = sanitizeComposerXml(spTreeXml, INJECTED_SHAPE_START_ID);
  const elements = extractShapeElements(sanitizedXml);
  if (elements.length === 0) throw new Error(NO_MERGEABLE_SHAPES_MESSAGE);
  const bbox = computeChildBoundingBox(elements);
  if (!bbox) throw new Error(NO_MERGEABLE_SHAPES_MESSAGE);
  const groupXml = buildGroupXml(elements, region, bbox);

  const presentationBytes = await getActivePresentationBytes();
  const zip = unzipSync(new Uint8Array(presentationBytes));
  const slideIndex = await getActiveSlideIndex();
  const { slideXml, slidePath } = parseSlideFromZip(zip, slideIndex);

  // base64 below is a whole-deck clone — an unresolved id would make the
  // caller insert it unfiltered, duplicating every slide, so fail the merge
  // instead (same as any other merge failure) rather than return null here.
  const presentationXmlEntry = zip["ppt/presentation.xml"];
  const sourceSlideId = presentationXmlEntry ? getSldIdAt(strFromU8(presentationXmlEntry), slideIndex) : null;
  if (!sourceSlideId) throw new Error(NO_MERGEABLE_SHAPES_MESSAGE);

  const nameSet = new Set(shapeNames.filter(Boolean).map((n) => n.toLowerCase()));
  let mergedXml = slideXml;
  mergedXml = removeMatchingShapes(mergedXml, SP_PATTERN, nameSet, region);
  mergedXml = removeMatchingShapes(mergedXml, GRAPHIC_FRAME_PATTERN, nameSet, region);
  mergedXml = removeMatchingShapes(mergedXml, GRP_PATTERN, nameSet, region);
  mergedXml = mergedXml.replace("</p:spTree>", `${groupXml}</p:spTree>`);

  zip[slidePath] = strToU8(mergedXml);
  const base64 = u8ToBase64(zipSync(zip, { level: 6 }));
  return { base64, sourceSlideId };
}
